import * as React from "react";
import { useParams } from "react-router-dom";
import { Copy, Plus, Trash2, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";

/**
 * Roles list + capability matrix editor. System roles are read-only (cloneable).
 * Edits apply immediately on save; the SSE `role.updated` event will refresh
 * the calling user's capabilities via CompanyContext.refresh().
 */
export default function Roles() {
  const { companyId } = useParams();
  const { catalog, has } = useCompany();
  const [roles, setRoles] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [name, setName] = React.useState("");
  const [caps, setCaps] = React.useState(new Set());
  const [saving, setSaving] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const canManage = has("roles.manage");

  const load = React.useCallback(async () => {
    const { data } = await api.get(`/companies/${companyId}/roles`);
    setRoles(data.roles);
    if (!selected && data.roles.length) select(data.roles[0], data.roles);
    if (selected) {
      const fresh = data.roles.find((r) => r.id === selected.id);
      if (fresh) select(fresh, data.roles);
    }
  }, [companyId, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  function select(r, list = roles) {
    setSelected(r);
    setName(r.name);
    setCaps(new Set(r.capabilities || []));
  }

  React.useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const on = (e) => {
      const t = e.detail?.type;
      if (t && t.startsWith("role.")) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  function toggleCap(k) {
    if (!selected || selected.is_system || !canManage) return;
    const next = new Set(caps);
    next.has(k) ? next.delete(k) : next.add(k);
    setCaps(next);
  }

  async function save() {
    if (!selected || selected.is_system || saving) return;
    setSaving(true);
    try {
      await api.patch(`/companies/${companyId}/roles/${selected.id}`, {
        name: name.trim(), capabilities: Array.from(caps),
      });
      toast.success("Role saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  }

  async function clone() {
    if (!selected) return;
    setCreating(true);
    try {
      const newName = `${selected.name} (copy)`;
      const { data } = await api.post(`/companies/${companyId}/roles`, {
        name: newName, clone_from: selected.id,
      });
      toast.success("Cloned");
      await load();
      // Select the new role
      setSelected(data);
      setName(data.name);
      setCaps(new Set(data.capabilities));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setCreating(false); }
  }

  async function deleteRole() {
    if (!selected || selected.is_system) return;
    if (!window.confirm(`Delete role "${selected.name}"?`)) return;
    try {
      await api.delete(`/companies/${companyId}/roles/${selected.id}`);
      toast.success("Removed");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  const capsGroups = catalog?.capabilities || {};
  const dirty = selected && !selected.is_system && (
    name !== selected.name || setsDiffer(caps, new Set(selected.capabilities || []))
  );

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="roles-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="micro-label">Access</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">Roles</h1>
          <div className="mt-1 text-[12px] text-brand-muted">
            System roles are locked. Clone to make an editable copy.
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-12 gap-4">
        {/* Roles list */}
        <div className="col-span-12 md:col-span-4">
          <div className="card-panel">
            <div className="card-panel-head">
              <div className="text-[13px] font-semibold text-brand-ink">All roles</div>
              <span className="mono-num text-[10px] chip">{roles.length}</span>
            </div>
            <ul className="py-1" data-testid="roles-list">
              {roles.map((r) => (
                <li key={r.id}>
                  <button
                    className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-brand-hush ${selected?.id === r.id ? 'bg-accent text-brand-violet' : 'text-brand-ink'}`}
                    onClick={() => select(r)}
                    data-testid={`role-item-${r.id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] font-medium truncate">{r.name}</span>
                      {r.is_system && <span className="mono-num text-[9px] chip">system</span>}
                    </span>
                    <span className="mono-num text-[10px] text-brand-muted">{r.holder_count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Matrix */}
        <div className="col-span-12 md:col-span-8">
          {!selected ? (
            <div className="card-panel p-6 text-brand-muted text-[12px]">Select a role.</div>
          ) : (
            <div className="card-panel">
              <div className="card-panel-head">
                <div className="flex items-center gap-2 min-w-0">
                  {selected.is_system ? (
                    <div className="text-[14px] font-semibold text-brand-ink">{selected.name}</div>
                  ) : (
                    <input className="field-input !h-8 max-w-[280px]" value={name}
                           onChange={(e) => setName(e.target.value)}
                           disabled={!canManage}
                           data-testid="role-name-input" />
                  )}
                  {selected.is_system && <span className="mono-num text-[9px] chip">read-only</span>}
                  <span className="mono-num text-[10px] chip">{selected.holder_count} holders</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {canManage && (
                    <button className="btn-outline" onClick={clone} disabled={creating}
                            data-testid="role-clone">
                      <Copy size={13} /> Clone
                    </button>
                  )}
                  {canManage && !selected.is_system && (
                    <>
                      <button className="btn-outline text-brand-coral border-brand-coral/40"
                              onClick={deleteRole} data-testid="role-delete">
                        <Trash2 size={13} /> Delete
                      </button>
                      <button className="btn-violet" onClick={save} disabled={!dirty || saving}
                              data-testid="role-save">
                        <Save size={13} /> {saving ? "…" : "Save"}
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="card-panel-body space-y-5" data-testid="role-matrix">
                {Object.entries(capsGroups).map(([group, items]) => (
                  <div key={group}>
                    <div className="flex items-center gap-2">
                      <div className="micro-label">{group}</div>
                      <div className="flex-1 h-px bg-brand-rule" />
                      <div className="mono-num text-[10px] text-brand-muted">
                        {items.filter((c) => caps.has(c.key)).length} / {items.length}
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {items.map((c) => (
                        <label key={c.key}
                               className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] ${(!selected.is_system && canManage) ? 'hover:bg-brand-hush cursor-pointer' : 'cursor-not-allowed'}`}
                               data-testid={`cap-${c.key}`}>
                          <input type="checkbox"
                                 checked={caps.has(c.key)}
                                 disabled={selected.is_system || !canManage}
                                 onChange={() => toggleCap(c.key)}
                                 className="h-3.5 w-3.5 accent-brand-violet" />
                          <span className="mono-num text-[11px] text-brand-muted min-w-[140px]">{c.key}</span>
                          <span className="text-brand-ink">{c.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function setsDiffer(a, b) {
  if (a.size !== b.size) return true;
  for (const x of a) if (!b.has(x)) return true;
  return false;
}
